import { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import { useHistory, useLocation } from "react-router-dom";
import { InvoiceDetailReportModel } from "../../api/models/invoiceDetailReport";
import { InvoiceSummaryReportModel } from "../../api/models/invoiceSummaryReport";
import InvoiceDetailReportResponse from "../../api/responses/invoiceDetailReportResponse";
import InvoiceSummaryReportResponse from "../../api/responses/invoiceSummaryReportResponse";
import { getInvoiceDetailReport, getInvoiceSummaryReport } from "../../api/services/invoiceService";
import { monthsOfYear } from "../../common/constants";
import Layout from "../../components/layout";
import Modal from "../../components/modal";
import queryString from 'query-string';
import InvoiceSummaryReport from "../../components/invoiceSummaryReport";
import moment from "moment";
import InvoiceDetailReport from "../../components/invoiceDetailReport";
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';

// Variables & general utility functions
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const now = new Date();
const datePickerFormat: string = 'MMM YYYY';
const servicePeriodFormat: string = 'MM/DD/YYYY';

type MonthSelectorProps = {
    range: { start: Moment, end: Moment },
    clickHandler: Function,
}

const MonthSelector = ({ range, clickHandler }: MonthSelectorProps) => {
    return (
        <div className='month-selector'>
            <p className='text-sm font-bold'>Currently Viewing</p>
            <div>
                <span className='text-3xl font-bold'>{range.start.format(datePickerFormat)} </span>
                <span className='text-sm font-bold text-ptsblue-normal underline cursor-pointer hover:text-ptsblue-normal'
                    title='Change date range'
                    onClick={() => clickHandler(true)}>Change</span>
            </div>
        </div>
    );
}

const getMonthDateRange = (year: number, month: number) => {
    const startDate: Moment = moment([year, month - 1]);
    const endDate: Moment = moment(startDate).endOf('month');

    return { start: startDate, end: endDate };
}

const getServicePeriod = (year: number, month: number) => {
    const range = getMonthDateRange(year, month);
    return `${range.start.format(servicePeriodFormat)} to ${range.end.format(servicePeriodFormat)}`;
}

const loadDetailReportDefer = async (args: any[]) => {
    return await getInvoiceDetailReport(args[0].districtId, args[0].year, args[0].month, args[0].programId);
}

const loadSummaryReportDefer = async (args: any[]) => {
    return await getInvoiceSummaryReport(args[0].districtId, args[0].year, args[0].month, args[0].programId);
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const InvoiceDraft = () => {
    // Variables / Constants
    //---------------------------------------------------------------------------------------------
    const history = useHistory();
    const location = useLocation();
    const parsedQueryString = queryString.parse(location.search);
    const urlDistrictId = Number(parsedQueryString.districtId);
    const urlProgramId = parsedQueryString.programId == null ? null : Number(parsedQueryString.programId);
    const urlYear = Number(parsedQueryString.year);
    const urlMonth = Number(parsedQueryString.month);
    //---------------------------------------------------------------------------------------------

    // States
    //---------------------------------------------------------------------------------------------
    const [month, setMonth] = useState<number>(urlMonth);
    const [year, setYear] = useState<number>(urlYear);
    const [selectedMonth, setSelectedMonth] = useState<number>(month);
    const [selectedYear, setSelectedYear] = useState<number>(year);
    const [servicePeriod, setServicePeriod] = useState<string>('');
    const [openMonthPicker, setOpenMonthPicker] = useState<boolean>(false);
    const [detailReport, setDetailReport] = useState<InvoiceDetailReportModel>();
    const [summaryReport, setSummaryReport] = useState<InvoiceSummaryReportModel>();
    //---------------------------------------------------------------------------------------------


    // API operations
    //---------------------------------------------------------------------------------------------
    useEffect(() => {
        setTimeout(() => {
            loadDetailReportAsync.run({ districtId: urlDistrictId, year, month, programId: urlProgramId });
            loadSummaryReportAsync.run({ districtId: urlDistrictId, year, month, programId: urlProgramId });
        }, 1);
    }, []);

    const loadDetailReportAsync = useAsync({
        deferFn: loadDetailReportDefer,
        onResolve: (response: InvoiceDetailReportResponse) => {
            setOpenMonthPicker(false);
            setYear(selectedYear);
            setMonth(selectedMonth);

            if (response) {
                const detailReportData = response?.data.report;
                setDetailReport(detailReportData);
                setServicePeriod(getServicePeriod(selectedYear, selectedMonth));
            }
        },
    });

    const loadSummaryReportAsync = useAsync({
        deferFn: loadSummaryReportDefer,
        onResolve: (response: InvoiceSummaryReportResponse) => {
            setOpenMonthPicker(false);
            setYear(selectedYear);
            setMonth(selectedMonth);

            if (response) {
                const summaryReportData = response?.data.report;
                setSummaryReport(summaryReportData);
                setServicePeriod(getServicePeriod(selectedYear, selectedMonth));
            }
        },
    });
    //---------------------------------------------------------------------------------------------


    // In-component utility functions
    //---------------------------------------------------------------------------------------------
    const navigateToTimesheetWeek = (weekId: number): void => {
        history.push(`/proofing/week/${weekId}`);
    }
    //---------------------------------------------------------------------------------------------


    // UI layout
    //---------------------------------------------------------------------------------------------
    return (
        <Layout isLoading={loadDetailReportAsync.isLoading || loadSummaryReportAsync.isLoading}>
            <Fragment>
                {/* Month picker modal */}
                <Modal
                    open={openMonthPicker}
                    saveText='Set Month/Year'
                    onSave={() => {
                        loadDetailReportAsync.run({ districtId: urlDistrictId, year: selectedYear, month: selectedMonth, programId: urlProgramId });
                        loadSummaryReportAsync.run({ districtId: urlDistrictId, year: selectedYear, month: selectedMonth, programId: urlProgramId });
                    }}
                    onCancel={() => setOpenMonthPicker(false)}
                >
                    <div className='flex space-x-3'>
                        <div className='w-full'>
                            <label className='mb-2'>Month:</label>
                            <Select
                                styles={graySelectStyles}
                                isSearchable={false}
                                value={monthsOfYear.map(m => ({ value: m.value, label: m.name })).find(o => o.value === selectedMonth)}
                                options={monthsOfYear.map(m => ({ value: m.value, label: m.name }))}
                                onChange={(selected) => setSelectedMonth(selected!.value)}
                                placeholder="Month"
                            />
                        </div>
                        <div className='w-full'>
                            <label className='mb-2'>Year:</label>
                            <Select
                                styles={graySelectStyles}
                                isSearchable={false}
                                value={Array.apply(0, Array(10)).map(function (x, i) {
                                    const y = now.getFullYear() - i;
                                    return { value: y, label: String(y) };
                                }).find(o => o.value === selectedYear)}
                                options={Array.apply(0, Array(10)).map(function (x, i) {
                                    const y = now.getFullYear() - i;
                                    return { value: y, label: String(y) };
                                })}
                                onChange={(selected) => setSelectedYear(selected!.value)}
                                placeholder="Year"
                            />
                        </div>
                    </div>
                </Modal>

                {/* Page content */}
                <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow main-page-body print:px-0'>
                    <div className="mb-8 month-selector-wrapper print:hidden">
                        <MonthSelector
                            range={getMonthDateRange(year, month)}
                            clickHandler={setOpenMonthPicker} />
                    </div>
                    <h1 className="font-bold underline">Summary Preview</h1>
                    <InvoiceSummaryReport
                        report={summaryReport}
                        servicePeriod={servicePeriod}
                    />
                    <hr className="my-12 border-4 border-gray-500 border-dashed"></hr>
                    <h1 className="font-bold underline">Detail Preview</h1>
                    <InvoiceDetailReport
                        report={detailReport}
                        servicePeriod={servicePeriod}
                        navigateToTimesheetWeek={navigateToTimesheetWeek}
                    />
                </div>
            </Fragment>
        </Layout>
    )
    //---------------------------------------------------------------------------------------------
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

export default InvoiceDraft;
